/* eslint-disable no-undef */

import React, { useEffect, useState, useRef, useMemo } from "react";
import FilterComponent from "@components/FilterComponent";
import OrderingComponent from "@components/OrderingComponent";
import SortingComponent from "@components/SortingComponent";
import ActionCell from "./actionCell";
import renderCell from "./renderCell";
import PinnedRow from "./pinnedRow";
import { directions } from "@helpers/helpers";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import GradientText from "@/components/GradiantTitle";
import { useTheme } from "@stores/ThemeContext";
import {
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Expand,
} from "lucide-react";

import SfmModalStore from "@stores/SfmModalStore";
import DownloadModalStore from "@stores/DownloadModalStore";
import {
  ZoomIn,
  ZoomOut,
  Search,
  ArrowUpNarrowWide,
  ArrowDownNarrowWide,
  FolderUp,
  EyeOff
} from "lucide-react";

import { useTranslation } from "react-i18next";

import Loader from "@components/LoaderComponent/loader";
import {
  fetchDynamicTags,
  addToDownloadCenter,
  extractFileName,
  extractFileSize,
  updateDownloadItem,
  GetSFMDisplay,
} from "@helpers/helpers";
import { downloadFile } from "@/helpers/downloadHelper";
import {
  scrollFn,
  getCommonPinningStyles,
  getLocalConfig,
  saveToLocalStorage,
  stopScroll,
  scrollHeaderIntoView,
  handleClearSort,
  handleSort,
  handleHideColumn,
  setHTMLFontSize,
  openFullScreen,
  closeFullScreen,
  checkFullScreen,
} from "./helpers";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { ExportReconciliationRecords } from "@helpers/helpers";

import ButtonComponent from "@components/ButtonComponent";

import KeyboardEventHandler, {
  displayName,
} from "react-keyboard-event-handler";

import Confirmationpopup from "@components/ConfirmationPopup";

import {
  TableContext,
  TableProvider,
  useTableContext,
} from "@components/CustomTable/context";
import { toast } from "react-toastify";

import { ZERO_VALUE, ONE_VALUE } from "@utils/constants";
import { Pin, X, ArrowLeft } from "lucide-react";

const highlightedColor = "#fbfbfb";
const darkModeHighlightedColor = "#374151";

/**
 * CustomTable component represents a customizable table with various features such as pinning, filtering, sorting, and pagination.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Array} props.columnsWithoutPin - An array of column configurations without pinning.
 * @param {Object} props.store - The store object containing data and methods for managing the table state.
 * @param {Function} props.pinCondition - A function that determines whether a row should be pinnable based on its data.
 * @param {Array} props.filterDisplayValues - An array of display values for the filter options.
 * @param {Array} props.selectedFiltersDisplay - An array of selected filter display values.
 * @param {Array} props.filters - An array of filter configurations.
 * @param {Object} props.pinningInitialState - The initial pinning state for columns.
 * @param {Function} props.setParentSelectedValues - A function to set the selected values in the parent component.
 * @param {Object} props.actions - An object containing action functions for the table.
 * @param {string} [props.prefix="table"] - The prefix for the table component.
 * @param {string} props.title - The title of the table.
 * @param {number} [props.pinnedRowHeight=45] - The height of pinned rows.
 * @param {number} [props.headerHeight=58] - The height of the table header.
 * @param {Function} [props.expandedViewRow=()=>{}] - A function to render the expanded view of a row.
 * @param {number} [props.maxTableHeight=500] - The maximum height of the table.
 * @param {Array} [props.multipleValueColumns=[]] - An array of column keys that support multiple values.
 * @param {boolean} [props.loading] - A flag indicating whether the table is in a loading state.
 * @param {boolean} [props.showFilter=true] - A flag indicating whether to show the filter options.
 * @returns {JSX.Element} The rendered CustomTable component.
 */

const DEFAULT_PINNED_ROW_HEIGHT = 45;
const DEFAULT_HEADER_HEIGHT = 45;
const DEFAULT_MAX_TABLE_HEIGHT = 500;
const MINIMUM_ROW_PINNING = 10;
const SCROLL_DELAY = 50;
const STORE_OPEN_TIMEOUT = 500;
const STORE_HIGHLIGHT_TIMEOUT = 500;
const SUBMENU_TOGGLE_TIMEOUT = 0;
const TOP_OFFSET_VALUE = 15;
const SUBMENU_OPEN_ZINDEX = 2;
const SECOND_TABINDEX = 2;
const THIRD_TABINDEX = 3;
const FOURTH_TABINDEX = 4;
const NO_POPUP_INDEX = -1;
const MODULO_VALUE = 2;

const CustomTable = ({
  language = "en",
  columnsWithoutPin,
  store = null,
  pinCondition,
  allowRowPinning = true,
  filterDisplayValues,
  selectedFiltersDisplay,
  filters,
  pinningInitialState,
  setParentSelectedValues,
  actions,
  prefix = "table",
  title,
  pinnedRowHeight = DEFAULT_PINNED_ROW_HEIGHT,
  headerHeight = DEFAULT_HEADER_HEIGHT,
  expandedViewRow = () => {},
  rowSeparator = () => {},
  maxTableHeight = DEFAULT_MAX_TABLE_HEIGHT,
  multipleValueColumns = [],
  loading,
  showFilter = true,
  selectableCondition = false,
  GlobalActions = {},
  additionalComponent,
  externalData = [],
  minimalTable,
  hideActionColumn,
  noData,
  isExpandable,
  secondaryTitle,
  autoFillData = true,
  additionalComponentPosition = "middle",
  setExternalSelectedState,
  rowCustomID,
  customSubRows,
  hidePinColumn,
  initialTableVisibility = true,
  showExportButton = false,
  activityTag = "",
  showAccessibility = false,
  externalDisplays,
  hideActionOnRow,
  allowExpansion = true,
  isExpandedByDefault = false,
  isRowExpandedByDefault = false,
  hasBackButton = null,
}) => {
  // const { data: storeData, limit } = store;
  const { t: translate, i18n } = useTranslation();
  const storeData = !!store ? store.data : null;
  const limit = !!store ? store.limit : ZERO_VALUE;
  const data = storeData || externalData || [];
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();

  const findDisplayName = (externalDisplays, value) => {
    let displayName = null;
    externalDisplays.map((singleDisplay) => {
      if (singleDisplay.children) {
        singleDisplay.children.map((child) => {
          if (child.value === value) {
            displayName = child.displayName;
          } else if (value.includes(child.value)) {
            displayName = displayName
              ? `${displayName}, ${child.displayName}`
              : child.displayName;
          }
        });
      }
    });
    return displayName;
  };

  const allowedUrlFilters = [
    "ReconciliationStreamTag",
    "IsInTransitCritical",
    "AgingTierTag",
  ];

  const uniquePinCondition = data.some((row) => pinCondition(row));
  const shouldHidePinColumn =
    !uniquePinCondition &&
    !selectableCondition &&
    data.length <= MINIMUM_ROW_PINNING;

  const PinCell = ({
    row,
    includeLeafRows,
    includeParentRows,
    pinCondition,
    expandedRows,
    setExpandedRows,
    selectableCondition,
  }) => {
    const rowSelected = row.getIsSelected();
    const shouldHidePin = hidePinColumn;

    useEffect(() => {
      if (isRowExpandedByDefault) {
        setExpandedRows((prev) => ({
          ...prev,
          [row.original.customID || row.id]: true,
        }));
      }
    }, [
      isRowExpandedByDefault,
      row.original.customID,
      row.id,
      setExpandedRows,
    ]);

    return shouldHidePin ? null : row.getIsPinned() ? (
      <div className="inline-flex w-full justify-center">
        <button
          data-testid={`unpin-button-${row.id}`}
          onClick={() => row.pin(false, includeLeafRows, includeParentRows)}
        >
          <X color="#d60000" />{" "}
        </button>
      </div>
    ) : (
      <div className="inline-flex flex-col w-full justify-between gap-2">
        {row.getCanPin() && allowRowPinning && (
          <button
            // className="hover:scale-125"
            data-testid={`pin-button-${row.id}`}
            onClick={() => row.pin("top", includeLeafRows, includeParentRows)}
            tabIndex={-1}
          >
            <Pin size={15} />
          </button>
        )}
        {((pinCondition && pinCondition(row.original)) || !pinCondition) && (
          <button
            className={clsx(
              `hover:scale-125 dark:text-white text-gray-400 hover:text-gray-400`,
              expandedRows[row.original.customID || row.id] &&
                "text-gray-600 hover:text-gray-600 scale-125 hover:scale-100"
            )}
            tabIndex={-1}
            onClick={() => {
              // add row to expanded state
              setExpandedRows((prev) => ({
                ...prev,
                [row.original.customID || row.id]: prev[
                  row.original.customID || row.id
                ]
                  ? !prev[row.original.customID || row.id]
                  : true,
              }));
            }}
          >
            <Expand size={15} />
          </button>
        )}
        {selectableCondition && selectableCondition(row.original) && (
          <input
            data-testid={`checkbox-${row.id}`}
            type="checkbox"
            className="form-checkbox text-primary-500 h-4 w-4 cursor-pointer"
            checked={rowSelected}
            onChange={row.getToggleSelectedHandler()}
          />
        )}
      </div>
    );
  };

  const pinColumn = {
    id: "pin",
    accessorKey: "pin",
    header: ({ table }) => {
      const selectedCount = table.getSelectedRowModel().rows.length;
      return (
        selectableCondition &&
        !!data.length &&
        selectableCondition(null, true) && (
          <div className="flex flex-col items-center gap-2">
            <input
              type="checkbox"
              className="form-checkbox text-primary-500 h-4 w-4 cursor-pointer"
              checked={table.getIsAllRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()}
            />
            {selectedCount > ZERO_VALUE && (
              <span data-testid="selected-rows-count">{selectedCount}</span>
            )}
          </div>
        )
      );
    },
    className: "text-center",
    size: 43,
    canPin: () => true,
    enableHiding: false,
    cell: ({ row }) => {
      return (
        <PinCell
          key={row.id + data.length}
          row={row}
          includeLeafRows={includeLeafRows}
          includeParentRows={includeParentRows}
          pinCondition={pinCondition}
          expandedRows={expandedRows}
          setExpandedRows={setExpandedRows}
          selectableCondition={selectableCondition}
        />
      );
    },
  };

  const actionColumn = () => {
    return {
      accessorKey: "Actions",
      // header: translate("shared.actions"),
      header: "",
      canPin: () => true,
      size: 35,
      enableHiding: false,
      // width: 200,
      cell: ({ row }) => {
        return (
          <ActionCell
            key={`${submenuVisible}_${row.id}`}
            row={row}
            toggleSubMenu={() => toggleSubMenu(row.id)}
            subMenuID={submenuVisible}
            actionFunctions={actionFunctions}
            actions={actions}
            store={store}
            hideActionOnRow={hideActionOnRow}
          />
        );
      },
    };
  };

  const FE_VERSION = VERSION || ONE_VALUE;
  const COLUMN_INITIAL_ORDER_KEY = `${FE_VERSION}_${prefix}_${language}_initialOrder`;
  const COLUMN_ORDER_KEY = `${FE_VERSION}_${prefix}_${language}_ordering`;
  const COLUMN_SORT_KEY = `${FE_VERSION}_${prefix}_${language}_sorting`;
  const COLUMN_VISIBILITY_KEY = `${FE_VERSION}_${prefix}_${language}_visibility`;
  const COLUMN_FILTER_KEY = `${FE_VERSION}_${prefix}_${language}_filter`;

  const DEFAULT_FONT_SIZE = 16;
  const MIN_FONT_SIZE = 12;
  const MAX_FONT_SIZE = 20;

  const includeLeafRows = false;
  const includeParentRows = false;

  const calculateInitialExpandedState = () => {
    const expandedState = {};
    if (isExpandedByDefault) {
      data.forEach((row) => {
        expandedState[row.customID] = true;
      });
    }
    return expandedState;
  };

  const [selectedValues, setSelectedValues] = useState({});
  // const [submenuVisible, setSubmenuVisible] = useState(false);
  const { submenuVisible, setSubmenuVisible } = useTableContext();
  const [isFullScreen, setIsFullScreen] = useState(checkFullScreen());
  const [expandedRows, setExpandedRows] = useState(
    calculateInitialExpandedState
  );
  const [selectableRows, setSelectableRows] = useState({});
  const [isCellMenuOpen, setIsCellMenuOpen] = useState(null);
  const isRtl = language === "ar";

  useEffect(() => {
    if (shouldHidePinColumn) {
      if (hideActionColumn) {
        setColumns([...resolveMidColumnsWithoutPin()]);
      } else {
        setColumns([...resolveMidColumnsWithoutPin(), actionColumn()]);
      }
    } else {
      if (hideActionColumn) {
        setColumns([pinColumn, ...resolveMidColumnsWithoutPin()]);
      } else {
        setColumns([
          pinColumn,
          ...resolveMidColumnsWithoutPin(),
          actionColumn(),
        ]);
      }
    }
  }, [data, submenuVisible]);

  const resolveMidColumnsWithoutPin = () => {
    const cols = isRtl
      ? [].concat(columnsWithoutPin).reverse()
      : columnsWithoutPin;
    return cols;
  };

  const [columns, setColumns] = useState(
    hideActionColumn
      ? hidePinColumn
        ? [...resolveMidColumnsWithoutPin()]
        : [pinColumn, ...resolveMidColumnsWithoutPin()]
      : [pinColumn, ...resolveMidColumnsWithoutPin(), actionColumn()]
  );
  const [columnPinning, setColumnPinning] = useState({
    left: isRtl ? ["Actions"] : pinningInitialState || ["pin"],
    right: isRtl ? pinningInitialState || ["pin"] : ["Actions"],
  });

  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 3000,
  });

  const [rowPinning, setRowPinning] = useState({
    top: [],
    bottom: [],
  });

  const initialPins = ["pin", "actions"];

  const [appliedFilters, setAppliedFilters] = useState(
    getLocalConfig(COLUMN_FILTER_KEY, location)
  );
  const [appliedSort, setAppliedSort] = useState(
    getLocalConfig(COLUMN_SORT_KEY, location)
  );

  const [columnVisibility, setColumnVisibility] = useState(
    getLocalConfig(COLUMN_VISIBILITY_KEY, location)
  );
  const [columnOrder, setColumnOrder] = useState(
    (!getLocalConfig(COLUMN_ORDER_KEY, location).includes(undefined) &&
      !!getLocalConfig(COLUMN_ORDER_KEY, location).length &&
      getLocalConfig(COLUMN_ORDER_KEY, location)) ||
      getLocalConfig(COLUMN_INITIAL_ORDER_KEY, location)
  );
  const [initialColumnOrder, setInitialColumnOrder] = useState(
    getLocalConfig(COLUMN_INITIAL_ORDER_KEY, location)
  );

  const [openFilterPopup, setOpenFilterPopup] = useState(false);
  const [dynamicTags, setDynamicTags] = useState([]);

  const [openOrderingPopup, setOpenOrderingPopup] = useState(false);
  const [openSortingPopup, setOpenSortingPopup] = useState(false);

  const [prepareApiData, setPrepareApiData] = useState({});
  const [openConfirmationAction, setOpenConfirmationAction] = useState(false);

  const [popupOpen, setPopupOpenFn] = useState(null);
  const [currentFontSize, setCurrentFontSize] = useState(DEFAULT_FONT_SIZE);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const [expandedChildren, setExpandedChildren] = useState({});

  const [tableVisible, setTableVisible] = useState(initialTableVisibility);

  const setPopupOpen = (id) => {
    setPopupOpenFn(id);
    document.body.style.overflow = id ? "hidden" : "auto";
  };
  const resetInitialState =
    JSON.stringify(getLocalConfig(COLUMN_ORDER_KEY, location)) !==
    JSON.stringify(getLocalConfig(COLUMN_INITIAL_ORDER_KEY, location));
  const [showResetButton, setShowResetButton] = useState(resetInitialState);

  const noPopupsOpen = openFilterPopup || openOrderingPopup || openSortingPopup;

  const selectableRowsKeys = Object.keys(selectableRows);

  const toggleSubMenu = (id) => {
    id !== "bulk" && table.resetRowSelection(true);
    setSubmenuVisible(id);
    setColumns(
      shouldHidePinColumn
        ? [...resolveMidColumnsWithoutPin(), actionColumn()]
        : [pinColumn, ...resolveMidColumnsWithoutPin(), actionColumn()]
    );
  };

  const actionFunctions = (action, row) => {
    if (action.confirmation) {
      setPrepareApiData({ action: action, row, null: null });
      setOpenConfirmationAction(true);
    } else {
      if (store) {
        store.loading = action?.hideLoading ? false : true;
      }
      action.actionFN(row);
    }
    handleClickOutside();
  };
  const bulkActionFunctions = (action, rowIDs) => {
    setPrepareApiData({ action: action, null: null, rowIDs });
    setOpenConfirmationAction(true);
  };

  const onColumnOrderChange = (newOrder) => {
    if (initialColumnOrder.length === ZERO_VALUE) {
      setInitialColumnOrder(newOrder);
    }
    setColumnOrder(newOrder);
  };

  const setExternalSelectableRows = (props) => {
    setSelectableRows(props);
    setExternalSelectedState && setExternalSelectedState(props);
  };

  const table = useReactTable({
    data,
    columns: columns.map((column) => {
      return {
        ...column,
        enableHiding: !(
          columnPinning.left.includes(column.accessorKey) ||
          columnPinning.right.includes(column.accessorKey)
        ),
      };
    }),
    state: {
      globalFilter,
      columnVisibility,
      pagination: pagination,
      rowPinning,
      columnPinning,
      columnOrder,
      rowSelection: selectableRows,
      expanded: expandedChildren,
    },
    getRowId: (row) =>
      !!rowCustomID ? rowCustomID(row) : row.MatchingRecordId,
    enableRowSelection: true,
    enableRowPinning: data.length > MINIMUM_ROW_PINNING,
    onRowSelectionChange: setExternalSelectableRows, //hoist up the row selection state to your own scope
    onColumnVisibilityChange: setColumnVisibility,
    onColumnPinningChange: setColumnPinning,
    onRowPinningChange: setRowPinning,
    keepPinnedRows: true,
    getFilteredRowModel: getFilteredRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onColumnOrderChange: onColumnOrderChange,
    getSubRows: (row) => (!!customSubRows ? customSubRows(row) : row.subRows),
    onExpandedChange: setExpandedChildren,
  });

  useEffect(() => {
    const initialVisibility = {};
    table.getAllLeafColumns().forEach((column) => {
      initialVisibility[column.id] = column.getIsVisible();
    });
    setColumnVisibility(initialVisibility);
  }, [table]);

  let scrollTimeout = null;

  const scroll = (direction, withDelay = true) => {
    if (scrollTimeout !== null) {
      clearTimeout(scrollTimeout);
    }

    if (withDelay) {
      scrollTimeout = setInterval(() => {
        scrollFn(".main-table-container", direction);
      }, SCROLL_DELAY); // 500ms debounce time
    } else {
      scrollFn(".main-table-container", direction, SCROLL_DELAY);
    }
  };

  const handleClickOutside = () => {
    setSubMenuOpen(false);
    setSubmenuVisible(null);
    SfmModalStore.close();
    setColumns(
      shouldHidePinColumn
        ? [...resolveMidColumnsWithoutPin(), actionColumn()]
        : [pinColumn, ...resolveMidColumnsWithoutPin(), actionColumn()]
    );
  };
  const scrollUpRef = useRef(null);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const parmsFilters = {};
    const columnOrder = table.getAllLeafColumns().map((column) => column.id);
    // setColumnOrder(columnOrder);
    setInitialColumnOrder(columnOrder);
    !getLocalConfig(COLUMN_INITIAL_ORDER_KEY, location, null) &&
      saveAndApplyOrdering(columnOrder, COLUMN_INITIAL_ORDER_KEY);

    if (params.toString() && !minimalTable) {
      // Check if there are any params in the URL

      for (const [key, value] of params.entries()) {
        if (allowedUrlFilters.includes(key)) {
          parmsFilters[key] = {
            displayName: findDisplayName(externalDisplays, value) || "",
            value: value,
            operator: "EQ",
            filterType:
              value === "true" || value === "false" ? "boolean" : "checkbox",
          };
        }
      }
      saveAndApplyFilter(parmsFilters);
    } else {
      store && store.setAppliedFilters(appliedFilters, false);
      store && store.setAppliedSort(appliedSort, false);
      store && autoFillData && store.fillData();
      const tableContainer = tableContainerRef.current;
    }
    autoFillData &&
      fetchDynamicTags("Lists", ["CORRECTIVE_ACTION_REASONS"], (newTags) => {
        setDynamicTags(newTags);
      });
  }, [externalDisplays]);

  const resetColumnOrder = () => {
    table.resetColumnOrder(false);
    table.resetColumnVisibility();
    saveAndApplyOrdering(
      getLocalConfig(COLUMN_INITIAL_ORDER_KEY, location) || initialColumnOrder
    );
  };

  const saveAndApplySort = (sort) => {
    setAppliedSort(sort);
    store.setAppliedSort(sort);
    saveToLocalStorage(COLUMN_SORT_KEY, sort);
  };

  const saveAndApplyOrdering = (order, localStorageKey = COLUMN_ORDER_KEY) => {
    setColumnOrder(order);
    saveToLocalStorage(localStorageKey, order);
  };

  const saveAndApplyFilter = (filters) => {
    saveToLocalStorage(COLUMN_FILTER_KEY, filters);
    setAppliedFilters(filters);
    store?.setAppliedFilters(filters);
  };

  const saveOrderingConfig = (id, value) => {
    let visibilityObject = getLocalConfig(COLUMN_VISIBILITY_KEY, location, {});
    if (id === null) {
      saveToLocalStorage(COLUMN_VISIBILITY_KEY, value);
    } else {
      visibilityObject[id] = value;
      saveToLocalStorage(COLUMN_VISIBILITY_KEY, visibilityObject);
    }
  };

  const handleKeyPress = (key, e) => {
    const openOrderPopupOnly = !openFilterPopup && !openSortingPopup;
    const openFilterPopupOnly = !openOrderingPopup && !openSortingPopup;
    const openSortingPopupOnly = !openFilterPopup && !openOrderingPopup;

    if (key === "f" && openFilterPopupOnly) {
      setOpenFilterPopup(!openFilterPopup);
    } else if (key === "c" && openOrderPopupOnly) {
      setOpenOrderingPopup(!openOrderingPopup);
    } else if (key === "s" && openSortingPopupOnly) {
      setOpenSortingPopup(!openSortingPopup);
    } else if (
      key === "up" ||
      key === "down" ||
      key === "left" ||
      key === "right"
    ) {
      e.preventDefault();
      scroll(key, false);
    } else if (key === "esc") {
      closeFullScreen();
      setIsFullScreen(false);
    }
  };

  const renderAccessibility = () => {
    return (
      <div className="flex gap-2">
        <button
          onClick={() => {
            setHTMLFontSize(
              "decrease",
              setCurrentFontSize,
              currentFontSize,
              DEFAULT_FONT_SIZE,
              MAX_FONT_SIZE,
              MIN_FONT_SIZE
            );
          }}
          className="text-primary-600 disabled:text-primary-200 dark:disabled:text-primary-600 dark:text-primary-200 disabled:cursor-not-allowed"
          disabled={currentFontSize === MIN_FONT_SIZE}
        >
          <ZoomOut />
        </button>
        <button
          onClick={() => {
            setHTMLFontSize(
              "default",
              setCurrentFontSize,
              currentFontSize,
              DEFAULT_FONT_SIZE,
              MAX_FONT_SIZE,
              MIN_FONT_SIZE
            );
          }}
          className="text-primary-600 disabled:text-primary-200 dark:disabled:text-primary-600 dark:text-primary-200 disabled:cursor-not-allowed"
          disabled={currentFontSize === DEFAULT_FONT_SIZE}
        >
          <Search />
        </button>
        <button
          onClick={() => {
            setHTMLFontSize(
              "increase",
              setCurrentFontSize,
              currentFontSize,
              DEFAULT_FONT_SIZE,
              MAX_FONT_SIZE,
              MIN_FONT_SIZE
            );
          }}
          className="text-primary-600 disabled:text-primary-200 dark:disabled:text-primary-600 dark:text-primary-200 disabled:cursor-not-allowed"
          disabled={currentFontSize === MAX_FONT_SIZE}
        >
          <ZoomIn />
        </button>
      </div>
    );
  };

  const hasData = !!data.length;
  const translatedDirections = directions(translate);
  const showNavigationArrows = !minimalTable && hasData && data.length > 10;
  return (
    <div>
      {loading && <Loader dots withContainer />}
      {openConfirmationAction && (
        <Confirmationpopup
          actions={actions}
          prepareApiData={prepareApiData}
          afterClose={() => handleClickOutside()}
          setOpenConfirmationAction={setOpenConfirmationAction}
          setPrepareApiData={setPrepareApiData}
          dynamicTags={dynamicTags}
          store={store}
        />
      )}
      <KeyboardEventHandler
        handleKeys={["f", "c", "s", "up", "down", "left", "right", "esc"]}
        onKeyEvent={(key, e) => handleKeyPress(key, e)}
        className={clsx(
          "HOCKeyboardHandler",
          loading && !store.data.length && "hidden"
        )}
      >
        <div
          className={`${
            !minimalTable ? "container-fake my-5" : "my-0"
          } mx-auto w-[calc(100%-${
            minimalTable ? "0px" : "100px"
          })] text-black fill-gray-400`}
        >
          <div className="w-[94%] mx-auto flex flex-col">
            <div className="flex justify-between">
              {title && (
                <div
                  className={clsx(
                    minimalTable && "cursor-pointer",
                    "flex items-center gap-2"
                  )}
                  {...(minimalTable &&
                    allowExpansion && {
                      onClick: () => setTableVisible(!tableVisible),
                    })}
                >
                  {hasBackButton && (
                    <ArrowLeft
                      className="dark:text-white"
                      onClick={() => {
                        window.location.href = hasBackButton;
                      }}
                    />
                  )}
                  <GradientText
                    className={clsx(
                      " text-tableTitle",
                      minimalTable
                        ? " !text-base font-semibold"
                        : "text-2xl font-normal"
                    )}
                  >
                    {title}
                    {minimalTable && secondaryTitle && (
                      <span className="text-sm text-gray-500 ml-2 dark:text-white">
                        {`${secondaryTitle}`}
                      </span>
                    )}
                  </GradientText>
                  {isExpandable && (
                    <ChevronDown
                      className={clsx(
                        `size-5 transition-all duration-500 dark:text-white`,
                        !tableVisible && "rotate-180"
                      )}
                    />
                  )}
                </div>
              )}
              <div className="flex gap-5 items-center justify-end">
                {hasData && (
                  <OrderingComponent
                    table={table}
                    showResetButton={showResetButton}
                    setShowResetButton={setShowResetButton}
                    setColumnOrder={saveAndApplyOrdering}
                    columnOrder={columnOrder}
                    resetColumnOrder={() => resetColumnOrder()}
                    saveOrderingConfig={saveOrderingConfig}
                    columnVisibilityKey={COLUMN_VISIBILITY_KEY}
                    openFilter={openOrderingPopup}
                    setOpenFilter={setOpenOrderingPopup}
                  />
                )}
                {(!minimalTable || showAccessibility) && renderAccessibility()}
              </div>
            </div>
            {additionalComponent &&
              additionalComponentPosition === "top" &&
              React.createElement(additionalComponent)}
            {!minimalTable && (
              <>
                <div className="flex w-full mb-5 mt-5">
                  {(hasData || appliedFilters) && (
                    <div
                      className={clsx(
                        showFilter && (hasData || appliedFilters)
                          ? "justify-between"
                          : "justify-end",
                        "flex w-full gap-4 items-baseline"
                      )}
                    >
                      {" "}
                      {showFilter && (hasData || appliedFilters) && (
                        <FilterComponent
                          appliedFilters={appliedFilters}
                          updateAppliedFilters={saveAndApplyFilter}
                          openFilter={openFilterPopup}
                          setOpenFilter={setOpenFilterPopup}
                          filterDisplayValues={filterDisplayValues}
                          filters={filters}
                          selectedFiltersDisplay={selectedFiltersDisplay}
                          setSelectedValues={(values) => {
                            setSelectedValues(values);
                            setParentSelectedValues(values);
                          }}
                          selectedValues={selectedValues}
                          multipleValueColumns={multipleValueColumns}
                        />
                      )}
                      {additionalComponent &&
                        additionalComponentPosition === "middle" &&
                        React.createElement(additionalComponent)}
                    </div>
                  )}
                </div>
                {showExportButton &&
                  !!data.length &&
                  !!Object.keys(appliedFilters).length && (
                    <div className="flex justify-between mb-5">
                      <ButtonComponent
                        data-testid="export-button"
                        className="text-sm min-w-max h-fit rounded-lg mb-4"
                        id="download-export"
                        color="blue"
                        onClick={() => {
                          const toastId = toast.loading(
                            translate(
                              "download-center.actions.processing-export"
                            )
                          );
                          const newPendingID = addToDownloadCenter({
                            href: "",
                            activityTag:
                              activityTag ||
                              translate(
                                "download-center.actions.default-export-activity"
                              ),
                            done: false,
                            apiEndpoint: "ExportReconciliationRecords",
                            apiParams: store.filter,
                            toastID: toastId,
                          });
                          ExportReconciliationRecords(
                            newPendingID,
                            store.filter,
                            store.sort,
                            activityTag,
                            (data) => {
                              if (data?.FileDetails?.Link) {
                                updateDownloadItem({
                                  id: newPendingID,
                                  newProps: {
                                    href: data.FileDetails.Link,
                                    title: data.FileDetails.Name,
                                    fileSize: data.FileDetails.Size,
                                    done: true,
                                  },
                                });
                                toast.update(toastId, {
                                  render: translate(
                                    "download-center.actions.download-ready"
                                  ),
                                  type: "success",
                                  isLoading: false,
                                  autoClose: 3000,
                                });
                                DownloadModalStore.open(STORE_OPEN_TIMEOUT);
                                DownloadModalStore.highlight(
                                  STORE_HIGHLIGHT_TIMEOUT,
                                  newPendingID
                                );
                                downloadFile(data.FileDetails.Link, "_blank");
                              }
                            },
                            store,
                            toastId,
                            translate("download-center.actions.download-ready")
                          );
                        }}
                        ref={null}
                        icon={<FolderUp size={18} />}
                        text="Export"
                        width="max"
                      />
                    </div>
                  )}
                {!!appliedSort.length && (
                  <div className="flex flex-col gap-4 items-end">
                    <div
                      key={JSON.stringify(appliedSort)}
                      className="flex flex-col items-end text-sm"
                    >
                      {appliedSort.map((singleSort, index) => {
                        return (
                          <div
                            key={index}
                            className=" mb-2 text-gray-500 flex items-center text-sm"
                          >
                            {!index
                              ? translate("shared.sorting")
                              : translate("shared.then")}{" "}
                            {translate("shared.by")}
                            <b className="mx-2 border-b border-gray-400 text-gray-600">
                              {singleSort.name}
                            </b>
                            <div
                              title={
                                translatedDirections[singleSort.direction]
                                  .display
                              }
                            >
                              {translatedDirections[singleSort.direction].icon}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <div
            className={clsx(
              minimalTable &&
                `grid grid-rows-[0fr] transition-all ease-in-out duration-500`,
              tableVisible ? "grid-rows-[1fr] opacity-100 " : "opacity-0"
            )}
          >
            <div className={clsx(minimalTable && "overflow-hidden")}>
              <div
                className={clsx(
                  `w-[92%] mx-auto relative main-table-parent-container my-5 dark:text-white `
                )}
                dir="ltr"
              >
                {showNavigationArrows && (
                  <>
                    <button
                      className="absolute -top-10 left-1/2 text-2xl hover:scale-120 hover:animate-[ping_1.5s_linear_infinite] text-primary-400 hover:text-primary-700 cursor-none"
                      onMouseEnter={() => scroll("up")}
                      onClick={() => scroll("up", false)}
                      onMouseOut={() => stopScroll(scrollTimeout)}
                      ref={scrollUpRef}
                      tabIndex={noPopupsOpen ? NO_POPUP_INDEX : FOURTH_TABINDEX}
                    >
                      <ChevronUp />
                    </button>
                    <button
                      className="absolute -left-10 top-1/2 text-2xl hover:scale-120 hover:animate-[ping_1.5s_linear_infinite] text-primary-400 hover:text-primary-700 cursor-none"
                      onMouseEnter={() => scroll("left")}
                      onClick={() => scroll("left", false)}
                      onMouseOut={() => stopScroll(scrollTimeout)}
                      tabIndex={noPopupsOpen ? NO_POPUP_INDEX : THIRD_TABINDEX}
                    >
                      <ChevronLeft />
                    </button>
                  </>
                )}

                {noData &&
                (data.length === ZERO_VALUE ||
                  externalData.length === ZERO_VALUE) ? (
                  // noData(translate || null)
                  noData()
                ) : (
                  <div
                    className="w-full max-w-full overflow-auto main-table-container"
                    ref={tableContainerRef}
                    style={{ maxHeight: maxTableHeight }}
                  >
                    {submenuVisible && (
                      <div
                        className="fixed top-0 left-0 w-full h-full bg-[#000000] dark:bg-opacity-40 bg-opacity-20 z-[4]"
                        onClick={handleClickOutside}
                      />
                    )}
                    <table
                      data-testid="custom-table"
                      className="border w-full text-center  relative text-sm text-[#777f9e] border-tableBorders "
                    >
                      <thead
                        className={clsx(
                          `sticky -top-0 `,
                          submenuVisible || popupOpen ? "z-[1]" : "z-[3]"
                        )}
                        style={{
                          boxShadow: isDarkMode
                            ? "0px -7px 4px 9px #2d3748"
                            : "0px -7px 4px 9px #e1e1e1",
                          backgroundColor: isDarkMode
                            ? darkModeHighlightedColor
                            : highlightedColor,
                        }}
                      >
                        {table.getHeaderGroups().map((headerGroup, index) => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header, index) => {
                              const shouldHideHeader =
                                header.column.columnDef.hideHeader;
                              const { column } = header;
                              const includedInitialPins =
                                initialPins.includes(
                                  header.column.columnDef.accessorKey
                                ) || header.column.columnDef.hidePin;

                              const columnHeader =
                                header.column.columnDef.header;
                              const columnHeaderHelper =
                                header.column.columnDef.headerHelper;

                              const isPinnedLeft =
                                header.column.getIsPinned() === "left";

                              const handleSubMenuToggle = (headerId) => {
                                let TOP_OFFSET = 30;
                                let LEFT_OFFSET = 20;
                                setTimeout(() => {
                                  const parent = document.getElementById(
                                    `parent-${header.id}`
                                  );
                                  const child = document.getElementById(
                                    `child-${header.id}`
                                  );
                                  if (child) {
                                    const parentRect =
                                      parent.getBoundingClientRect();

                                    const top = parentRect.top;
                                    const left = parentRect.left;
                                    if (i18n.language === "ar") {
                                      TOP_OFFSET = TOP_OFFSET_VALUE;
                                      child.style.right = `unset`;
                                    }
                                    child.style.top = `${top + TOP_OFFSET}px`;
                                    child.style.left = `${
                                      left + LEFT_OFFSET
                                    }px`;
                                  }
                                }, SUBMENU_TOGGLE_TIMEOUT);
                                setSubMenuOpen(
                                  subMenuOpen === headerId ? null : headerId
                                );
                                scrollHeaderIntoView(
                                  `header_${header.id}`,
                                  isPinnedLeft,
                                  isRtl
                                );
                              };

                              //Sub Menu Items

                              const currentColumnSorting = appliedSort.find(
                                (sort) => sort.id === header.id
                              );
                              const currentColumnSortingDirection =
                                currentColumnSorting?.direction;
                              const subMenuItems = [
                                {
                                  icon: () => (
                                    <span className="text-lg">
                                      {!isPinnedLeft ? (
                                        <Pin size={18} />
                                      ) : (
                                        <X size={18} fill="red" />
                                      )}
                                    </span>
                                  ),
                                  displayText: !isPinnedLeft
                                    ? translate("shared.pinLeft", {
                                        columnHeader,
                                      })
                                    : translate("shared.unpin", {
                                        columnHeader,
                                      }),
                                  onClick: () => {
                                    header.column.pin(
                                      isPinnedLeft
                                        ? false
                                        : isRtl
                                        ? "left"
                                        : "left"
                                    );
                                    setSubMenuOpen(null);
                                  },
                                  hasSeperator: true,
                                  visible:
                                    !includedInitialPins &&
                                    (header.column.columnDef.canPin ===
                                      undefined ||
                                      header.column.columnDef.canPin === true),
                                },

                                hasData && {
                                  icon: () => (
                                    <EyeOff size={18} />
                                  ),
                                  displayText: `Hide ${columnHeader} column`,
                                  disabled: false,
                                  onClick: () =>
                                    handleHideColumn(
                                      header,
                                      saveOrderingConfig,
                                      setSubMenuOpen,
                                      setShowResetButton
                                    ),
                                  hasSeperator: false,
                                  visible: true,
                                },
                              ];

                              const isHeaderClickable =
                                !minimalTable && header.id !== "Actions";
                              const headerColumnDef = header.column.columnDef;

                              return (
                                <th
                                  key={header.id}
                                  data-testid={`column-header-${header.id}${
                                    column.getIsPinned() ? "-pinned" : ""
                                  }`}
                                  id={`header_${header.id}`}
                                  colSpan={header.colSpan}
                                  className={clsx(
                                    `relative px-3.5 dark:!bg-gray-700 dark:text-white py-2 text-center border shadow-inner-remove min-w-fit font-normal text-tableHeaders border-tableBorders`,
                                    column.getIsPinned() &&
                                      " dark:after:bg-gray-700 after:content[''] after:absolute after:left-[-1px] after:top-0 after:w-[calc(100%+2px)] after:h-full after:bg-white after:z-[-1] after:opacity-70"
                                  )}
                                  //IMPORTANT: This is where the magic happens!
                                  style={{
                                    ...getCommonPinningStyles(
                                      column,
                                      ZERO_VALUE,
                                      highlightedColor,
                                      null,
                                      null,
                                      subMenuOpen === header.id
                                        ? SUBMENU_OPEN_ZINDEX
                                        : ONE_VALUE,
                                      ONE_VALUE,
                                      isDarkMode
                                    ),
                                    verticalAlign: "top",
                                    width: column.getSize(),
                                    minWidth: column.getSize(),
                                  }}
                                >
                                  <div
                                    className={clsx(
                                      "flex gap-1 ",
                                      headerColumnDef.alignment
                                        ? `justify-${headerColumnDef.alignment} items-${headerColumnDef.alignment} text-${headerColumnDef.alignment}`
                                        : "justify-center items-center text-center"
                                    )}
                                  >
                                    {!shouldHideHeader && (
                                      <div
                                        data-testid={`header-${header.id}`}
                                        id={`parent-${header.id}`}
                                        className={clsx(
                                          `mt-0 flex items-center gap-1`,
                                          isHeaderClickable && "cursor-pointer"
                                        )}
                                        onClick={() =>
                                          isHeaderClickable &&
                                          handleSubMenuToggle(header.id)
                                        }
                                      >
                                        {header.isPlaceholder
                                          ? null
                                          : flexRender(
                                              columnHeader,
                                              header.getContext()
                                            )}{" "}
                                        {currentColumnSortingDirection ===
                                          "desc" && <ArrowDownNarrowWide />}
                                        {currentColumnSortingDirection ===
                                          "asc" && <ArrowUpNarrowWide />}
                                      </div>
                                    )}

                                    {!includedInitialPins && (
                                      <div className="flex relative">
                                        {subMenuOpen === header.id && (
                                          <div
                                            className="fixed top-0 left-0 w-full h-full bg-[#000000] bg-opacity-20  z-[2]"
                                            onClick={handleClickOutside}
                                          />
                                        )}
                                        <div
                                          data-testid={`sub-menu-${header.id}`}
                                          id={`child-${header.id}`}
                                          className={clsx(
                                            `fixed rtl:right-[unset] ltr:left-0 top-full dark:bg-gray-700 bg-white p-1 rounded shadow-lg w-max z-[5] popup`,
                                            subMenuOpen === header.id
                                              ? "visible"
                                              : "hidden"
                                          )}
                                        >
                                          <>
                                            {subMenuItems.map(
                                              (subItem, index) => {
                                                return subItem.visible ? (
                                                  <div key={index}>
                                                    <button
                                                      key={index}
                                                      data-testid={`sub-menu-item-${index}`}
                                                      className={clsx(
                                                        `flex items-center gap-2 p-2 mb-2 w-full dark:hover:bg-gray-500 hover:bg-gray-100 font-normal`,
                                                        subItem?.disabled
                                                          ? "cursor-default opacity-40"
                                                          : "cursor-pointer"
                                                      )}
                                                      onClick={() =>
                                                        subItem.onClick(
                                                          header,
                                                          subItem?.direciton
                                                        )
                                                      }
                                                      disabled={
                                                        subItem.disabled
                                                      }
                                                    >
                                                      {subItem.icon && (
                                                        <div>
                                                          {subItem.icon()}
                                                        </div>
                                                      )}
                                                      {subItem.displayText.replace(
                                                        "${0}",
                                                        columnHeader
                                                      )}
                                                    </button>
                                                    {subItem.hasSeperator && (
                                                      <div className="border-t-2 mb-4 border-gray-100"></div>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <div key={index}></div>
                                                );
                                              }
                                            )}
                                          </>
                                        </div>
                                      </div>
                                    )}
                                    {columnHeaderHelper && (
                                      <div key={index}>
                                        {columnHeaderHelper()}
                                      </div>
                                    )}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        ))}
                      </thead>
                      <tbody>
                        {table.getTopRows() &&
                          table
                            .getTopRows()
                            .map((row, i) => (
                              <PinnedRow
                                key={row.id}
                                id={`row-top-${row.id}`}
                                row={row}
                                table={table}
                                index={i}
                                headerHeight={headerHeight}
                                pinnedRowHeight={pinnedRowHeight}
                                highlightedColor={highlightedColor}
                                submenuVisible={submenuVisible}
                                popupOpen={popupOpen}
                                setPopupOpen={setPopupOpen}
                                appliedFilters={appliedFilters}
                                saveAndApplyFilter={saveAndApplyFilter}
                              />
                            ))}
                        {table.getCenterRows().length ? (
                          table.getCenterRows().map((row, i) => {
                            const rowExpanded =
                              expandedRows[row.original.customID || row.id];
                            const rowSelected = selectableRows[row.id];
                            return (
                              <React.Fragment key={row.id}>
                                <tr
                                  key={row.id}
                                  data-testid={`table-row-${i}`}
                                  id={`row-center-${row.id}`}
                                  className={clsx(
                                    "bg-white dark:bg-gray-700 dark:hover:bg-gray-500 hover:bg-gray-50",
                                    submenuVisible === row.id &&
                                      "relative z-[5]"
                                  )}
                                >
                                  {row.getVisibleCells().map((cell) => {
                                    const { column } = cell;
                                    const columnDef = cell.column.columnDef;
                                    const context = cell.getContext();
                                    const value = context.getValue();
                                    const passedFilterCheck =
                                      !columnDef.filterCheck ||
                                      (columnDef.filterCheck &&
                                        columnDef.filterCheck(value));
                                    const truncate = columnDef.truncate;
                                    const rowId = i;

                                    const cellID = `cell-${i}-${column.columnDef.accessorKey}`;
                                    return (
                                      <td
                                        key={cell.id}
                                        data-testid={`cell-${i}-${column.columnDef.accessorKey}`}
                                        className={clsx(
                                          `px-3.5 py-2 border  border-b-tableBorders border-l-transparent border-r-transparent shadow-inner-remove w-fit relative `,
                                          rowExpanded && "border-b-[#f7f3f356]",
                                          column.getIsPinned() &&
                                            "dark:after:bg-gray-700 after:content['']  after:absolute after:left-[-1px] after:top-0 after:w-[calc(100%+2px)] after:h-full after:bg-white after:z-[-1] after:opacity-90"
                                        )}
                                        onClick={() => {
                                          setIsCellMenuOpen(
                                            isCellMenuOpen === cellID
                                              ? null
                                              : cellID
                                          );
                                        }}
                                        style={{
                                          ...getCommonPinningStyles(
                                            column,
                                            ZERO_VALUE,
                                            rowSelected
                                              ? isDarkMode
                                                ? "#6b7280"
                                                : "#e6e6e6"
                                              : isDarkMode
                                              ? "#374151"
                                              : "transparent",
                                            "unset",
                                            ZERO_VALUE,
                                            ONE_VALUE,
                                            isDarkMode
                                          ),
                                          maxWidth: column.getSize(),
                                        }}
                                        width={column.getSize() + "px"}
                                      >
                                        <div
                                          className={clsx(
                                            columnDef?.accessorKey === "pin" &&
                                              "flex w-[43px]"
                                          )}
                                        >
                                          {renderCell(
                                            row,
                                            cell,
                                            popupOpen,
                                            setPopupOpen,
                                            appliedFilters,
                                            saveAndApplyFilter,
                                            translate,
                                            isCellMenuOpen,
                                            rowId,
                                            isDarkMode
                                          )}
                                        </div>
                                      </td>
                                    );
                                  })}
                                </tr>
                                {rowExpanded && (
                                  <tr className="text-center">
                                    <td colSpan={columns.length}>
                                      {expandedViewRow &&
                                        expandedViewRow(
                                          row,
                                          row.context,
                                          actionFunctions
                                        )}
                                    </td>
                                  </tr>
                                )}
                                {rowSeparator &&
                                  rowSeparator(row, columns.length)}
                              </React.Fragment>
                            );
                          })
                        ) : (
                          <tr className="text-center h-40">
                            <td colSpan={columns.length}>
                              {translate("shared.no-records")}
                            </td>
                          </tr>
                        )}
                        {table.getBottomRows() &&
                          table
                            .getBottomRows()
                            .map((row, index) => (
                              <PinnedRow
                                key={row.id}
                                id={`row-bottom-${row.id}`}
                                row={row}
                                table={table}
                                index={index}
                                headerHeight={headerHeight}
                                pinnedRowHeight={pinnedRowHeight}
                                highlightedColor={highlightedColor}
                                submenuVisible={submenuVisible}
                                popupOpen={popupOpen}
                                setPopupOpen={setPopupOpen}
                                appliedFilters={appliedFilters}
                                saveAndApplyFilter={saveAndApplyFilter}
                              />
                            ))}
                      </tbody>
                    </table>
                    {selectableRowsKeys.length > ONE_VALUE && (
                      <div
                        className={clsx(
                          `container mx-auto absolute mb-2 -top-0 -translate-y-[100%] z-[4] p-3 flex flex-row rtl:flex-row-reverse gap-2 justify-start items-center overflow-visible text-right`,
                          !!selectableRowsKeys.length
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      >
                        <div className="flex gap-2 rtl:flex-row-reverse">
                          <button
                            className="text-primary-500  cursor-pointer flex items-center gap-1 flex-row ml-[3px]"
                            onClick={() => {
                              table.toggleAllRowsSelected(false);
                            }}
                          >
                            <input
                              data-testid="multi-row-select-all"
                              type="checkbox"
                              className="form-checkbox text-primary-500 text-xs h-4 w-4 cursor-pointer"
                              checked={true}
                            />
                          </button>
                          <span className="text-sm">
                            <span
                              data-testid="multi-row-select-count"
                              className="font-semibold"
                            >
                              {selectableRowsKeys.length}
                            </span>{" "}
                            {translate(
                              selectableRowsKeys.length > ONE_VALUE
                                ? "shared.selected-records"
                                : "shared.selected-record"
                            )}
                          </span>
                        </div>
                        <div className="relative">
                          <button
                            data-testid="bulk-actions-button"
                            disabled={selectableRowsKeys.length === ZERO_VALUE}
                            onClick={() => toggleSubMenu("bulk")}
                            className="relative ml-0 px-2 py-1 text-primary-500 dark:bg-gray-700 bg-white rounded  z-[5] whitespace-nowrap"
                          >
                            {translate("shared.bulk-actions")}
                          </button>
                          <div
                            data-testid="bulk-actions-menu"
                            className={clsx(
                              `absolute dark:bg-gray-700 flex flex-col top-full ltr:left-0 rtl:right-0 rtl:left-[unset] justify-around w-max rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 divide-y divide-gray-200 z-[10]`,
                              submenuVisible === "bulk" ? "visible" : "hidden"
                            )}
                          >
                            {Object.keys(GlobalActions)?.map(
                              (actionIdentifier, index) => {
                                const action = GlobalActions[actionIdentifier];
                                return (
                                  <button
                                    data-testid={`bulk-action-${action.actionTypeTag}`}
                                    key={index}
                                    onClick={() => {
                                      bulkActionFunctions(
                                        action,
                                        Object.keys(selectableRows).join(",")
                                      );
                                    }}
                                    className="w-full text-sm text-start rtl:text-end dark:hover:bg-gray-500 hover:bg-gray-100 font-normal cursor-pointer p-2 px-4"
                                  >
                                    {action.name}
                                  </button>
                                );
                              }
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {showNavigationArrows && (
                  <>
                    <button
                      className="absolute -right-10 top-1/2 text-2xl hover:scale-125 hover:animate-[ping_1.5s_linear_infinite] text-primary-400 hover:text-primary-700 cursor-none"
                      onMouseEnter={() => scroll("right")}
                      onClick={() => scroll("right", false)}
                      onMouseOut={() => stopScroll(scrollTimeout)}
                      tabIndex={noPopupsOpen ? NO_POPUP_INDEX : SECOND_TABINDEX}
                    >
                      <ChevronRight />
                    </button>
                    <button
                      className="absolute -bottom-10 left-1/2 text-2xl hover:scale-125 hover:animate-[ping_1.5s_linear_infinite] text-primary-400 hover:text-primary-700 cursor-none"
                      onMouseEnter={() => scroll("down")}
                      onClick={() => scroll("down", false)}
                      onMouseOut={() => stopScroll(scrollTimeout)}
                      tabIndex={noPopupsOpen ? NO_POPUP_INDEX : ONE_VALUE}
                    >
                      <ChevronDown />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </KeyboardEventHandler>
    </div>
  );
};

export default CustomTable;
